/* eslint-disable operator-linebreak */
/* eslint-disable quote-props */
import { Box, Button, CircularProgress } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CartIcon, RefreshIcon, VisibilityOnIcon } from "../../../../../../../components/Icons";
import { EPrescriptionStatus } from "../../../../../../../services/prescriptions/prescriptions.interface";
import { useMedicineUrl } from "../../../../../../../services/prescriptions/prescriptions.service";
import { VETCOVE_URL, DRAWERS } from "../../../../../../../config/params";
import { useDrawer } from "../../../../../../../context/DrawerContext";
import { IPrescriptionResponse } from "../../../../../../../services/prescription/prescription.interface";
import { usePetCareStore } from "../../../../../../../hooks/redux-hooks/pet-care-data/pet-care-data-store";
import { userActivityLogger } from "../../../../../../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../../../../../../config/mixPanelEvents";
import { useVetCoveRedirect } from "../../../../../../../hooks/useVetCoveRedirect";
import { useTheme } from "@mui/material";

interface IPrescriptionActionButtonsProps {
  status: EPrescriptionStatus;
  prescriptionId: string;
  itemId: string;
  size?: "small" | "medium";
  productUrl?: string;
  prescription?: IPrescriptionResponse;
  source: "shop" | "care";
  shopUrl?: string;
}

const PrescriptionActionButtons = ({
  status,
  prescriptionId,
  itemId,
  productUrl,
  size = "medium",
  prescription,
  source,
  shopUrl,
}: IPrescriptionActionButtonsProps) => {
  const theme = useTheme();

  const outlinedButtonSx = (size: "small" | "medium") => ({
    fontSize: size === "small" ? "12px" : "14px",
    height: size === "small" ? "32px" : "38px",
    borderRadius: "10px",
    borderColor: theme.palette.taupe.light,
    color: theme.palette.black.darker,
    textWrap: "nowrap",
    "&:hover": {
      borderColor: theme.palette.black.main,
      backgroundColor: theme.palette.white.main,
    },
  });
  const { openDrawer } = useDrawer();
  const [, setSearchParams] = useSearchParams();
  const { executeGetMedicineUrl, url, loading } = useMedicineUrl();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const { setSelectedPrescription } = usePetCareStore();
  const { handleVetCoveLink } = useVetCoveRedirect();

  useEffect(() => {
    if (shouldRedirect && !loading) {
      handleVetCoveLink(url, shopUrl || VETCOVE_URL);
      setShouldRedirect(false);
    }
  }, [shouldRedirect, url, loading, shopUrl, handleVetCoveLink]);

  const handleOpenVetcove = useCallback(() => {
    const eventName =
      status === EPrescriptionStatus.EXPIRED
        ? MIXPANEL_EVENTS.PHARMACY_PRESCRIPTION_RENEW_CLICKED
        : MIXPANEL_EVENTS.PHARMACY_PRESCRIPTION_ORDER_CLICKED;

    userActivityLogger(eventName, {
      prescription_id: prescriptionId,
      source,
      item_id: itemId,
      prescription_status: status,
    });

    if (productUrl) {
      handleVetCoveLink(productUrl);
      return;
    }
    setShouldRedirect(true);
    executeGetMedicineUrl(itemId);
  }, [status, prescriptionId, source, itemId, productUrl, executeGetMedicineUrl, handleVetCoveLink]);

  const handleViewDetails = useCallback(() => {
    if (prescription) {
      setSelectedPrescription(prescription);
    }
    setSearchParams({ prescriptionId, source });
    openDrawer(DRAWERS.PRESCRIPTION_DETAILS);
  }, [prescription, setSelectedPrescription, setSearchParams, prescriptionId, source, openDrawer]);

  return (
    <Box className="flex items-center" sx={{ gap: { xs: 1, md: 2 } }}>
      {status === EPrescriptionStatus.EXPIRED ? (
        <Button
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <RefreshIcon width={16} height={16} color={theme.palette.black.darker} />
            )
          }
          onClick={handleOpenVetcove}
          sx={{
            fontSize: size === "small" ? "12px" : "14px",
            height: size === "small" ? "32px" : "38px",
            borderRadius: "10px",
            textWrap: "nowrap",
            "& .MuiButton-startIcon": {
              "& svg path": {
                transition: "fill 0.2s ease",
              },
            },
            "&:hover": {
              "& svg path": {
                fill: "white",
              },
            },
          }}
        >
          Renew RX
        </Button>
      ) : (
        <Button
          variant="outlined"
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CartIcon width={16} height={16} color={theme.palette.black.darker} />
            )
          }
          onClick={handleOpenVetcove}
          sx={outlinedButtonSx(size)}
        >
          Buy Medicine
        </Button>
      )}
      <Button
        variant="outlined"
        startIcon={<VisibilityOnIcon width={16} height={16} />}
        onClick={handleViewDetails}
        sx={outlinedButtonSx(size)}
      >
        View Details
      </Button>
    </Box>
  );
};

export default PrescriptionActionButtons;
